import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteOutline, MoreVert } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
  Menu,
  MenuItem,
  Radio,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { format } from 'date-fns-tz';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { StatusColors } from '../../utils/Config';
import { useOkta } from '../../hooks/oktaHook';
import {
  monthDateFormat,
  convertToCurrencyWithoutSign,
  convertToRoundOffWithNumberOfDecimals,
  convertToPercentageWithDecimal,
  convertToCurrency,
  convertToCurrencyWithPercent,
  convertToRoundOffWithNumberOfDecimalsWithPercent,
  getUrlWithParams
} from '../../utils/Utils';
import { setSelectedCycleObj } from '../../redux/HomePage/cyclesByBuSlice';
import {
  DOLLAR_FORMAT_COLUMNS,
  COMMA_FORMAT_COLUMNS,
  ZERO_DECIMAL_FORMAT_COLUMNS,
  ONE_DECIMAL_FORMAT_COLUMNS,
  TWO_DECIMAL_FORMAT_COLUMNS,
  ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  TWO_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  TPO_ADMIN,
  GENERAL_USER,
  CORE_USER
} from '../../utils/Constants';
import { Icon } from 'pepsico-ds';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: '#005CBC',
  width: '24px',
  height: '24px'
}));

const CellContent = ({
  columns = [],
  column,
  row,
  isEditable,
  originalData,
  onValueChange,
  onRadioChange,
  onDrawerChange,
  tableName,
  editingRowId,
  setEditingRowId,
  yearMonthData,
  newRowData,
  isCycleEdit,
  setIsCycleEdit,
  getCycleHistory,
  showCycleSave,
  setShowCycleSave,
  handleLogs,
  isSteeringRecoCompleted,
  handleGoalsModal,
  showCycleDrawer,
  handleModalAction,
  isRecommendationTable,
  setDMCPopup,
  setDMCRegionCol
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBu = useSelector((state) => state.cycle.selectedBu);
  const [value, setValue] = useState(row[column.id]);
  const [anchorEl, setAnchorEl] = useState(null);
  const { userDetails } = useOkta();
  useEffect(() => {
    setValue(row[column.id]);
  }, [row[column.id]]);
  const statusColumnId = columns.find((item) => item.type === 'status')?.id;

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    column?.onDownload(row);
    handleMenuClose();
  };

  const handleDelete = () => {
    column?.onDelete(row);
    handleMenuClose();
  };

  const handleCycleEdit = async () => {
    setEditingRowId(row.id);
    setIsCycleEdit(true);
    setShowCycleSave(false);
    await getCycleHistory(row.id);
    column?.onEdit(row);
    handleMenuClose();
  };
  const handleCycleLock = () => {
    column?.onLock(row);
    handleMenuClose();
  };
  const handleActive = () => {
    column?.selectActive(row);
  };
  const checkIsEmpty = (rowId, columnId) => {
    const row = originalData.find((item) => item.id === rowId);
    if (row) {
      const value = row[columnId];
      return value === undefined || value === null || value === '';
    }

    return false;
  };

  const handleStartYearPeriodChange = (e) => {
    onValueChange({ startYearPeriod: e.target.value });
  };

  const handleEndYearPeriodChange = (e) => {
    onValueChange({ endYearPeriod: e.target.value });
  };

  const columnType = column?.type;
  const columnEditable = column?.editable;
  if(value == null && column.defaultValue) {
    return column.defaultValue;
  }
  if (columnType === 'text' && isEditable && columnEditable && checkIsEmpty(row.id, column.id)) {
    return (
      <TextField
        id={column.id}
        placeholder="Fill info"
        size="small"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onBlur={() => onValueChange(value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <WarningAmberOutlinedIcon
                sx={{
                  color: '#FF891C',
                  width: '16px',
                  height: '16px',
                  marginRight: '4px'
                }}
              />
            </InputAdornment>
          )
        }}
      />
    );
  } else if (columnType === 'date') {
    let formattedDate = '';

    const formatDate = (timestamp) => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const date = new Date(timestamp * 1000); // Convert from epoch (in seconds) to milliseconds
      const timeZoneAbbreviation = format(date, 'zzz', { timeZone });
      if (!isNaN(date.getTime())) {
        const options = {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        };
        const formattedDate = date.toLocaleString('en-US', options);
        const [datePart, timePart] = formattedDate.split(', ');
        const [time, modifier] = timePart.split(' ');
        const [hours, minutes] = time.split(':');
        return `${datePart} ${hours}:${minutes} ${modifier} ${timeZoneAbbreviation}`;
      }
      return 'Invalid Date';
    };

    // Check which field is being processed and format accordingly
    if (row.lastModifiedSince && column.id === 'lastModifiedSince') {
      formattedDate = formatDate(row.lastModifiedSince);
    } else if (row.cycleStartTime && column.id === 'cycleStartTime') {
      formattedDate = formatDate(row.cycleStartTime);
    } else if (row.cycleEndTime && column.id === 'cycleEndTime') {
      formattedDate = formatDate(row.cycleEndTime);
    } else if (row.LastUpdatedTimestamp && column.id == 'LastUpdatedTimestamp') {
      formattedDate = formatDate(row.LastUpdatedTimestamp);
    }

    return formattedDate;
  } else if (columnType === 'link') {
    return (
      <div
        style={{
          textDecoration: 'underline',
          color: '#005cbc',
          cursor: 'pointer'
        }}
        onClick={() => {
          dispatch(setSelectedCycleObj(row));
          navigate(getUrlWithParams('/cycle-landing', selectedBu, row?.cycleName, row?.id));
        }}>
        {row[column.id]}
      </div>
    );
  } else if (columnType === 'cycleNameLink') {
    return (
      <div
        style={{
          color: '#005cbc',
          cursor: 'pointer'
        }}
        onClick={() => showCycleDrawer(row)}>
        {row.cycleName}
      </div>
    );
  } else if (columnType === 'numBoolean') {
    if (value === 0) {
      return 'False';
    } else if (value === 1) {
      return 'True';
    } else {
      return value;
    }
  } else if (columnType === 'checkbox') {
    return (
      <>
        {column.id === 'lockCycle' ? (
          <Checkbox
            name={value}
            checked={value}
            size="small"
            sx={{ padding: 'initial' }}
            onChange={(e) => columnEditable && onValueChange(!row[column.id])}
          />
        ) : (
          <Checkbox
            name={value}
            checked={value}
            size="small"
            sx={{ padding: 'initial' }}
            onChange={(e) => columnEditable && onValueChange(!row[column.id])}
            disabled={row.lockCycle === true}
          />
        )}
      </>
    );
  } else if (columnType === 'radio') {
    let radioDisabled = false;
    if (tableName === 'Cycles List' && row.status) {
      radioDisabled =
        row.testCycle ||
        ['failed', 'created', 'inprogress', 'submitted'].includes(row.status.toLowerCase()) ||
        !['completed', 'locked']?.includes(row.status.toLowerCase());
    }
    return (
      <>
        <Radio
          size="small"
          sx={{ padding: 'initial' }}
          onChange={(e) => {
            if (columnEditable) {
              onRadioChange(true);
              handleActive();
            }
          }}
          disabled={radioDisabled}
          name={value}
          checked={value}
        />
      </>
    );
  } else if (columnType === 'status') {
    return (
      <Box
        sx={{
          borderRadius: '4px',
          padding: '2px 8px',
          color: '#000000',
          width: 'fit-content',
          backgroundColor: StatusColors[value]
        }}>
        {value}
      </Box>
    );
  } else if (columnType === 'button') {
    if (
      tableName == 'Team' &&
      userDetails?.roles.some((role) => [CORE_USER, TPO_ADMIN].includes(role)) &&
      row.userRole == GENERAL_USER
    ) {
      return (
        <>
          <Icon
            alt="alt value"
            className="$color-icon-default"
            icon="edit"
            size="medium"
            onClick={() => handleModalAction(true, row)}>
            EDIT
          </Icon>
        </>
      );
    } else {
      return (
        <Typography
          sx={{
            color: '#005CBC',
            fontSize: 'inherit',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}>
          {value}
        </Typography>
      );
    }
  } else if (columnType === 'start_button') {
    return (
      <>
        {['Created', 'Completed', 'Failed']?.includes(row.status) ||
        (row.status === 'Updated' && !row.lockCycle) ? (
          <Typography
            sx={{
              color: '#005CBC',
              fontSize: 'inherit',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={() => column.onClick(row, userDetails)}>
            {row.status == 'Created' ? 'Start Cycle' : 'Restart Cycle'}
          </Typography>
        ) : (
          <Typography
            sx={{
              color: '#616161',
              fontSize: 'inherit',
              cursor: 'not-allowed',
              '&:hover': {
                textDecoration: 'none'
              }
            }}>
            N/A
          </Typography>
        )}
      </>
    );
  } else if (columnType === 'delete') {
    return (
      <StyledIconButton
        aria-label="delete"
        data-testid={`delete-cycle-${row.id}`}
        onClick={() => column.onDelete(row)}>
        <DeleteOutline />
      </StyledIconButton>
    );
  } else if (columnType === 'actions') {
    return (
      <>
        <StyledIconButton aria-label="actions" onClick={(event) => handleMenuClick(event)}>
          <MoreVert />
        </StyledIconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem
            onClick={handleDownload}
            disabled={['Uploading', 'Processing', 'Deleted'].includes(row[statusColumnId])}>
            <DownloadIcon sx={{ fontSize: '18px', marginRight: '8px' }} />
            Download
          </MenuItem>
          <MenuItem
            onClick={handleDelete}
            disabled={['Uploading', 'Processing', 'Deleted', 'Failed'].includes(
              row[statusColumnId]
            )}>
            <DeleteOutlineIcon sx={{ fontSize: '18px', marginRight: '8px' }} />
            Delete
          </MenuItem>
        </Menu>
      </>
    );
  } else if (columnType === 'cycleActions') {
    return (
      <box>
        <StyledIconButton aria-label="actions" onClick={(event) => handleMenuClick(event)}>
          <MoreVert />
        </StyledIconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem
            onClick={handleCycleEdit}
            disabled={
              (!row.testCycle && isSteeringRecoCompleted) ||
              (!row.testCycle && isSteeringRecoCompleted && row?.activeStatus)
            }>
            <EditOutlinedIcon sx={{ fontSize: '18px', marginRight: '8px' }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={handleCycleLock}
            disabled={row.status !== 'Completed' && row.status !== 'Locked'}>
            {row.status == 'Locked' ? (
              <LockOpenOutlinedIcon sx={{ fontSize: '18px', marginRight: '8px' }} />
            ) : (
              <LockOutlinedIcon sx={{ fontSize: '18px', marginRight: '8px' }} />
            )}
            {row.status == 'Locked' ? 'Unlock' : 'Lock'}
          </MenuItem>
        </Menu>
      </box>
    );
  } else if (column.id === 'goals') {
    let locked =
      (!row.testCycle && isSteeringRecoCompleted) ||
      (!row.testCycle && isSteeringRecoCompleted && row?.activeStatus);
    return (
      <div
        data-testid="set-goals"
        style={{
          color: locked ? '#a0a0a0' : '#005cbc',
          cursor: locked ? '' : 'pointer'
        }}
        onClick={() => (locked ? {} : handleGoalsModal(row))}>
        Set Goals
      </div>
    );
  } else if (column.id === 'activeStatus') {
    return row[column.id] === true ? 'Yes' : 'No';
  } else if (column.id === 'viewlogs') {
    return (
      <span
        style={{ fontSize: '14px', fontWeight: 400, color: '#005cbc', cursor: 'pointer' }}
        onClick={() => handleLogs(row)}>
        View
      </span>
    );
  } else if (column.id === 'createdTime') {
    return monthDateFormat(row[column.id]);
  } else if (
    column.id === 'steering team agreement' ||
    column.id?.includes('agree steering team reco') ||
    column.id?.includes('steering team agreement') ||
    column.id?.includes('completion status')
  ) {
    let icon = <></>;
    if (typeof row[column.id] === 'boolean') {
      icon = row[column.id] ? (
        <ThumbUpIcon style={{ width: '1.2em', height: '1.2em', color: ' #008553' }} />
      ) : (
        <ThumbDownIcon style={{ width: '1.2em', height: '1.2em', color: '#DD334D' }} />
      );
    }
    return <Box style={{ textAlign: 'center' }}>{icon}</Box>;
  } else if (column.id === 'functional team votes') {
    return (
      <Box
        style={{
          textAlign: 'center',
          display: 'inline-flex',
          width: '140px',
          justifyContent: 'space-around'
        }}>
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            backgroundColor: '#B3DACB',
            borderRadius: '10px',
            padding: '0px 10px'
          }}>
          <ThumbUpIcon style={{ width: '1em', height: '1em', color: ' #008553' }} />
          <span style={{ fontSize: '20px', margin: '5px 2px' }}>
            {row[column.id]?.functionalteamupvotes}
          </span>
        </Box>
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            backgroundColor: '#F6CCD2',
            borderRadius: '10px',
            padding: '0px 10px'
          }}>
          <ThumbDownIcon
            style={{
              width: '1em',
              height: '1em',
              color: '#DD334D'
            }}
          />
          <span style={{ fontSize: '20px', margin: '5px 2px' }}>
            {row[column.id]?.functionalteamdownvotes}
          </span>
        </Box>
      </Box>
    );
  } else if (column.id === 'functional team comments' || column.id === 'collated comments') {
    return (
      <Box
        onClick={(e) => onDrawerChange(true)} //* actual one
        style={{
          fontWeight: 400,
          color: '#005CBC'
        }}>
        {`See ${
          row['functional team votes']?.functionalteamupvotes +
          row['functional team votes']?.functionalteamdownvotes
        } inputs`}
      </Box>
    );
  } else if (column.id === 'votes aligned') {
    return (
      ![undefined, null].includes(row[column.id]) && (
        <div
          style={{
            background: row[column.id] === true ? '#B3DACB' : '#F6CCD2',
            width: '40px',
            textAlign: 'center'
          }}>
          {row[column.id] ? 'Yes' : 'No'}
        </div>
      )
    );
  } else if (column.id === 'functionValue') {
    //add to show value in wrap by word
    return (
      <Box style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>{value}</Box>
    );
  } else if (isCycleEdit && editingRowId !== null) {
    if (column.id === 'startYearPeriod' || column.id === 'endYearPeriod') {
      if (row.id === editingRowId) {
        return (
          <FormControl key={column.id} fullWidth variant="outlined" size="small">
            <InputLabel>YYYYPP</InputLabel>
            <Select
              size="small"
              labelId={`select-label-${column.id}`}
              id={`select-${column.id}`}
              name={column.id}
              value={row[column.id]?.[column.id] || row[column.id]}
              label={'YYYYPP'}
              onChange={(e) =>
                column.id === 'startYearPeriod'
                  ? handleStartYearPeriodChange(e)
                  : handleEndYearPeriodChange(e)
              }
              sx={{ width: '8rem' }}>
              {yearMonthData?.map((date, idx) => (
                <MenuItem
                  key={`${idx + 1}`}
                  value={date}
                  disabled={
                    column.id === 'endYearPeriod' &&
                    (Number(row['startYearPeriod']?.startYearPeriod) >= Number(date) ||
                      Number(row['startYearPeriod']) > Number(date))
                  }>
                  {date}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        return row.id === 'endYearPeriod' ? (
          <Typography
            sx={{
              color: '#616161',
              fontSize: 'inherit',
              cursor: 'not-allowed',
              '&:hover': {
                textDecoration: 'none'
              }
            }}>
            {row['endYearPeriod']}
          </Typography>
        ) : (
          <Typography
            sx={{
              color: '#616161',
              fontSize: 'inherit',
              cursor: 'not-allowed',
              '&:hover': {
                textDecoration: 'none'
              }
            }}>
            {row['startYearPeriod']}
          </Typography>
        );
      }
    } else if (column.id === 'cycleName') {
      return (
        <Typography
          sx={{
            color: '#005cbc',
            fontSize: 'inherit',
            cursor: 'not-allowed',
            '&:hover': {
              textDecoration: 'none'
            }
          }}>
          {row.cycleName}
        </Typography>
      );
    } else if (column.id === 'threshold' && row.id === editingRowId) {
      return (
        <TextField
          sx={{
            '& .MuiInputBase-root': {
              height: 32 // Adjust the height of the input here
            }
          }}
          data-testid="threshold"
          inputProps={{ maxLength: 6, type: 'number' }}
          value={row?.threshold}
          placeholder={''}
          onChange={(event) => {
            onValueChange({ threshold: event.target.value });
          }}
        />
      );
    } else {
      return value;
    }
  } else if (
    column.id == 'REGIONAL TEAM VOTES' &&
    ['Decision Table', 'Completion Table'].includes(tableName)
  ) {
    return (
      <Box
        style={{
          textAlign: 'center',
          display: 'inline-flex',
          width: '140px',
          justifyContent: 'space-around'
        }}>
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            backgroundColor: '#B3DACB',
            borderRadius: '10px',
            padding: '0px 10px'
          }}>
          <ThumbUpIcon style={{ width: '1em', height: '1em', color: ' #008553' }} />
          <span style={{ fontSize: '20px', margin: '5px 2px' }}>{row[column.id]?.upVote}</span>
        </Box>
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            backgroundColor: '#F6CCD2',
            borderRadius: '10px',
            padding: '0px 10px'
          }}>
          <ThumbDownIcon
            style={{
              width: '1em',
              height: '1em',
              color: '#DD334D'
            }}
          />
          <span style={{ fontSize: '20px', margin: '5px 2px' }}>{row[column.id]?.downVote}</span>
        </Box>
      </Box>
    );
  } else if (
    column.id == 'INPUTS FROM REGIONAL TEAMS' &&
    ['Decision Table', 'Completion Table'].includes(tableName)
  ) {
    return (
      <span
        style={{
          color: '#005CBC',
          cursor: 'pointer',
          textDecoration: 'none'
        }}
        onClick={() => {
          setDMCPopup(true);
          setDMCRegionCol(row);
        }}>
        See Details
      </span>
    );
  } else if (column.id === 'startYearPeriod' && Object.keys(value)?.length > 0) {
    return value.startYearPeriod;
  } else if (column.id === 'endYearPeriod' && Object.keys(value)?.length > 0) {
    return value.endYearPeriod;
  } else if (columnType === 'number') {
    if (DOLLAR_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToCurrency(value);
    }
    if (COMMA_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToCurrencyWithoutSign(value);
    }
    if (COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToCurrencyWithPercent(value);
    }
    if (ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToPercentageWithDecimal(value, 0);
    }
    if (ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToPercentageWithDecimal(value, 1);
    }
    if (TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToPercentageWithDecimal(value, 2);
    }
    if (ZERO_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimals(value, 0);
    }
    if (ONE_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimals(value, 1);
    }
    if (TWO_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimals(value, 2);
    }
    if (TWO_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimalsWithPercent(value, 2);
    }
    if (ONE_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimalsWithPercent(value, 1);
    }
  } else if (isRecommendationTable) {
    if (['revenue'].includes(column.id)) {
      if (DOLLAR_FORMAT_COLUMNS.indexOf(column.label) > -1) {
        return convertToCurrency(value);
      }
    }
    if (['revenue growth', 'volume growth'].includes(column.id)) {
      if (ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
        return convertToPercentageWithDecimal(value, 0);
      }
    }
    if (['profit %', 'flowback %'].includes(column.id)) {
      if (ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
        return convertToPercentageWithDecimal(value, 1);
      }
    }
    if (['waste %'].includes(column.id)) {
      if (TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
        return convertToPercentageWithDecimal(value, 2);
      }
    }
    if (['distribution %', 'facility %'].includes(column.id)) {
      if (ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
        return convertToPercentageWithDecimal(value, 0);
      }
    }
    if (['facility count', 'store count', 'volume qty'].includes(column.id)) {
      if (ZERO_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
        return convertToRoundOffWithNumberOfDecimals(value, 0);
      }
    }
  }

  return value;
};

export default CellContent;
